import * as React from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@common/components/modal';
import { I18n } from 'react-redux-i18n';
import cx from 'classnames';
import TreeDropdown from '@common/components/TreeDropdown';
import { Flex } from '@common/components/Flex';
import { Checkbox } from '@common/components/checkbox';
import Dropdown from '@common/containers/fields/Dropdown';
import { InviteReviewedStatusStateGetDto } from '@common/dto/invite-reviewed-status-state/get-model';
import { InviteReviewedStatusStateWhoToMail } from '@common/dto/invite-reviewed-status-state/who-to-mail';
import { InviteReviewedStatusStateUpdateDto } from '@common/dto/invite-reviewed-status-state/update-dto';
import { Loading } from '@common/components/loading';
import { EmailTemplateListDto } from '@common/dto/email-template/list';
import { FolderPrimaryListDto } from '@common/dto/folder/primary-list-dto';
import { Button } from '@common/components/Button';
import { getTestAttributes } from '@common/utils/tests';

import './update-status-config.styles.scss';


type UpdateStatusConfigProps = {
  id: string;
  fhq4: boolean;
  getState: (id: string, fhq4: boolean) => Promise<InviteReviewedStatusStateGetDto>;
  getFolders: () => Promise<Array<FolderPrimaryListDto>>;
  close: () => void;
  onSubmit: (data: InviteReviewedStatusStateUpdateDto) => Promise<void>;
  showErrorMessage: (msg: string) => void;
  getEmailTemplates: () => Promise<Array<EmailTemplateListDto>>
};

export const UpdateStatusConfig = (props: UpdateStatusConfigProps) => {
  const moduleName = 'IndividualsInvitesUpdateStatus';
  const { id, fhq4, close, onSubmit, showErrorMessage, getEmailTemplates, getFolders, getState } = props;

  const [submitting, setSubmitting] = React.useState(false);
  const [loadingData, setLoadingData] = React.useState(false);

  const [shouldMoveToFolder, setShouldMoveToFolder] = React.useState(false);
  const [shouldTransmitPedigree, setShouldTransmitPedigree] = React.useState(false);
  const [shouldSendEmail, setShouldSendEmail] = React.useState(false);
  const [whoToSend, setWhoToSend] = React.useState<InviteReviewedStatusStateWhoToMail | null>(null);
  const [emailTemplateId, setEmailTemplateId] = React.useState<number | null>(null);
  const [folderId, setFolderId] = React.useState<number | null>(null);

  const [permissions, setPermissions] = React.useState<{ shouldMoveToFolder: boolean; shouldTransmitPedigree: boolean; shouldSendEmail: boolean }>();

  const [emailTemplatesOptions, setEmailTemplatesOptions] = React.useState<Array<{ value: number; label: string }>>([]);
  const [emailTemplates, setEmailTemplates] = React.useState<Array<EmailTemplateListDto>>([]);
  const [folders, setFolders] = React.useState<Array<FolderPrimaryListDto>>([]);

  const initialize = React.useCallback(async () => {
    setLoadingData(true);

    await Promise.all([
      getEmailTemplates()
        .then((res) => {
          setEmailTemplatesOptions(res.map((x) => ({ value: x.id, label: x.name })));
          setEmailTemplates(res);
        })
        .catch((err) => {
          console.error(err);
          showErrorMessage(err?.entity || I18n.t("Failed to get email templates"));
        }),
      getFolders()
        .then((res) => {
          setFolders(res);
        })
        .catch((err) => {
          console.error(err);
          showErrorMessage(err?.entity || I18n.t("Failed to get folders"));
        }),
      getState(id, fhq4)
        .then((state) => {
          setPermissions({
            shouldMoveToFolder: state.moveToFolder,
            shouldSendEmail: state.sendEmail,
            shouldTransmitPedigree: state.transmitPedigreeToEMR,
          })

          setWhoToSend(state.whoToEmail ?? null);
          setEmailTemplateId(state.emailTemplate?.id ?? null);
          setFolderId(state.folderId ?? null);
        })
        .catch((err) => {
          console.error(err);
          showErrorMessage(err?.entity || I18n.t("Failed to get data"));
        })
    ]);

    setLoadingData(false);
  }, []);

  React.useEffect(() => {
    initialize();
  }, []);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);

      const emailTemplate = emailTemplates.find((x) => x.id === emailTemplateId);

      await onSubmit({
        inviteIds: [
          {
            id: Number(id),
            fhq4,
          },
        ],
        baseUrl: window.location.origin,
        statusActionsReviewed: {
          folderId: folderId ?? undefined,
          sendEmail: shouldSendEmail,
          whoToEmail: whoToSend ?? undefined,
          emailTemplate: emailTemplate
            ? {
              id: emailTemplate.id,
              name: emailTemplate.name,
              subject: emailTemplate.subject,
              body: emailTemplate.body,
              createddate: emailTemplate.createddate,
              createdby: emailTemplate.createdby,
              modifieddate: emailTemplate.modifieddate,
              modifiedby: emailTemplate.modifiedby,
              description: emailTemplate.description,
              attachmentRepository: emailTemplate.attachmentRepository,
              locked: emailTemplate.locked,
            }
            : undefined,
          moveToFolder: shouldMoveToFolder,
          transmitPedigreeToEMR: shouldTransmitPedigree,
        }
      });
    } catch (err: any) {
      console.error(err);
      showErrorMessage(err?.entity || I18n.t("Failed to update status"));
      setSubmitting(false);
    }
  };

  const whoToSendOptions = React.useMemo(() => {
    const ops: Record<InviteReviewedStatusStateWhoToMail, string> = {
      [InviteReviewedStatusStateWhoToMail.patient]: I18n.t('Patient'),
      [InviteReviewedStatusStateWhoToMail.staff]: I18n.t('Staff'),
      [InviteReviewedStatusStateWhoToMail.both]: I18n.t('Both'),
    };
    
    return Object.keys(ops).map((key: string) => ({ value: Number(key), label: ops[key as unknown as InviteReviewedStatusStateWhoToMail] }));
  }, []);

  return (
    <Modal
      show
      backdrop="static"
      bsClass="bootstrap modal"
      className="IndividualsInvitesTableUpdateStatusConfig"
      dialogClassName="IndividualsInvitesTableUpdateStatusConfig"
      animation={false}
      onEscapeKeyDown={close}
      onHide={close}
    >
      <ModalHeader onHide={close} closeButton className="IndividualsInvitesTableUpdateStatusConfig__header">
        <ModalTitle>{I18n.t("'Reviewed' Status Actions")}</ModalTitle>
      </ModalHeader>
      <ModalBody className="IndividualsInvitesTableUpdateStatusConfig__body">
        {loadingData && <Flex width="100%" className="IndividualsInvitesTableUpdateStatusConfig__body__loading"><Loading size={20} /></Flex>}
        <h5>
          {I18n.t("Changing the status to 'Reviewed' will trigger the following actions:")}
        </h5>
        <Flex 
          alignItems="center" 
          className={cx('IndividualsInvitesTableUpdateStatusConfig__section', { 'IndividualsInvitesTableUpdateStatusConfig__section--disabled': !permissions?.shouldSendEmail })}
        >
          <Flex alignItems="center" width="50%">
            <Checkbox
              className="IndividualsInvitesTableUpdateStatusConfig__checkbox-dropdown"
              checked={shouldSendEmail}
              {...getTestAttributes(moduleName, 'send_email', 'checkbox')}
              label={I18n.t('Send Email')}
              onChange={setShouldSendEmail}
            />

            <Dropdown
              disabled={!shouldSendEmail}
              className="IndividualsInvitesTableUpdateStatusConfig__dropdown"
              {...getTestAttributes(moduleName, 'send_email', 'dropdown')}
              selectedValue={emailTemplateId}
              searchable={false}
              options={emailTemplatesOptions}
              optionsHeight={100}
              onValueChanged={setEmailTemplateId}
            />
          </Flex>

          <Flex alignItems="center" width="50%">
            <p className="IndividualsInvitesTableUpdateStatusConfig__dropdown-label">{I18n.t('Who to Email')}</p>
            <Dropdown
              disabled={!shouldSendEmail}
              className="IndividualsInvitesTableUpdateStatusConfig__dropdown"
              {...getTestAttributes(moduleName, 'who_to_email', 'dropdown')}
              selectedValue={whoToSend}
              searchable={false}
              options={whoToSendOptions}
              optionsHeight={100}
              onValueChanged={setWhoToSend}
            />
          </Flex>
        </Flex>

        <Flex 
          alignItems="center" 
          className={cx('IndividualsInvitesTableUpdateStatusConfig__section', { 'IndividualsInvitesTableUpdateStatusConfig__section--disabled': !permissions?.shouldMoveToFolder })}
        >
          <Checkbox 
            className="IndividualsInvitesTableUpdateStatusConfig__checkbox-dropdown"
            checked={shouldMoveToFolder}
            {...getTestAttributes(moduleName, 'move_to_folder', 'checkbox')}
            label={I18n.t('Move To Folder')}
            onChange={setShouldMoveToFolder}
          />
          <TreeDropdown 
            disabled={!shouldMoveToFolder}
            items={folders}
            value={folderId}
            placeholder="Select a Folder"
            className="IndividualsInvitesTableUpdateStatusConfig__folders"
            onChange={setFolderId}
          />
        </Flex>

        <div className={cx('IndividualsInvitesTableUpdateStatusConfig__section', { 'IndividualsInvitesTableUpdateStatusConfig__section--disabled': !permissions?.shouldTransmitPedigree })}>
          <Checkbox 
            {...getTestAttributes(moduleName, 'transmit_pedigree  ', 'checkbox')}
            checked={shouldTransmitPedigree}
            label={I18n.t('Transmit Pedigree PDF to EMR')}
            onChange={setShouldTransmitPedigree}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          disabled={submitting}
          {...getTestAttributes(moduleName, 'review_status_actions_cancel', 'button')}
          onClick={close}
        >
          {I18n.t('Cancel')}
        </Button>
        <Button
          {...getTestAttributes(moduleName, 'review_status_actions_submit', 'button')}
          disabled={submitting}
          bsStyle="primary"
          className="IndividualsInvitesTableUpdateStatusConfig__submit-btn"
          onClick={handleSubmit}
        >
          {I18n.t('Ok')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};